import { Component, Inject } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { NOTIFICATION_STATUS } from 'src/app/interfaces/notifications/notification.interface';
import { StartProcessService } from 'src/app/components/visits-reports/start/start-process.service';
import { ApiResponses, BackendService } from 'src/app/services/backend.service';
import { ProcessHandlerService } from 'src/app/process-engine/process/process-handler-dialog/process-handler.service';
import { IdToNamePipe } from 'src/app/pipes/id-to-name.pipe';
import { Step } from 'src/app/interfaces/process/step.interface';
import {
  AllStepsPerformedEvent,
  ProcessHandlerDialogComponent
} from 'src/app/process-engine/process/process-handler-dialog/process-handler-dialog.component';
import { Asset } from 'src/app/interfaces/assets/asset.interface';
import { firstValueFrom } from 'rxjs';
import {
  DF_ORDER_DEFECT_ARTICLES,
  DF_ORDER_MISSING_ARTICLES,
  DF_SHARED_ORDER_NUMBER,
  DF_ORDER_PHONE,
  DF_SUPPLIER_CONTACT_ARTICLE,
  DF_SUPPLIER_CONTACT_ARTICLE_NUMBER,
  DF_SUPPLIER_CONTACT_CONTACT,
  DF_SUPPLIER_CONTACT_ORDER_NUMBER,
  DF_SUPPLIER_CONTACT_PHONE,
  DF_SUPPLIER_CONTACT_PROOF_IMAGE,
  DF_SUPPLIER_CONTACT_SUPPLIER,
  DF_SUPPLIER_CONTACT_USER_NAME,
  DF_SUPPLIER_CONTACT_USER_PHONE,
  DF_SHARED_VISITOR
} from 'src/app/shared/constants/process-engine.constants';
import { UserNotificationService } from 'src/app/services/user-notification.service';
import { MobileViewService } from 'src/app/services/mobile-view.service';
import { ProtectedImageLoaderService } from 'src/app/services/protected-image-loader.service';
import { NotificationService } from 'src/app/services/notification.service';
import { JwtService } from 'src/app/services/jwt.service';
import {
  getLegacyEquipmentNotificationAssetIds,
  isInventoryDeclarationNotification,
  isLegacyEquipmentDefectMissingNotification,
  isProcessStepResponsibleForUser,
  LEGACY_EQUIPMENT_SUPPLIER_CONTACT_PROCESS_DEFS
} from 'src/app/shared/utils/legacy-order.util';
import { InventoryDeclarationNotificationPanelComponent } from './inventory-declaration-notification-panel/inventory-declaration-notification-panel.component';
import { DocumentReviewNotificationPanelComponent } from './document-review-notification-panel/document-review-notification-panel.component';
import { DocumentExpiryNotificationPanelComponent } from './document-expiry-notification-panel/document-expiry-notification-panel.component';
import {
  ORDER_INQUIRY_NOTIFICATION_TYPE_KEYS,
  OrderInquiryNotificationPanelComponent
} from './order-inquiry-notification-panel/order-inquiry-notification-panel.component';
import {
  SURVEY_NOTIFICATION_TYPE_KEY,
  SurveyNotificationPanelComponent
} from './survey-notification-panel/survey-notification-panel.component';

/**
 * Both entry points (header bell and notification center) render this dialog, but their list
 * endpoints deliberately stay lean. The detail itself is loaded once from
 * `GET notifications/{id}/detail`, so the two paths can no longer drift apart (SHIP-1805).
 */
type NotificationDetail = ApiResponses['notification/getNotificationDetail'];
type NotificationRow = NotificationDetail['notification'];
type EquipmentSupplier = ApiResponses['equipment/getSuppliers'][0];

/** Shown instead of an empty value, so the dialog never renders "undefined" or "[object Object]". */
const EMPTY_VALUE = '—';

@Component({
  selector: 'app-notification-detail-dialog',
  templateUrl: './notification-detail-dialog.component.html',
  styleUrls: ['./notification-detail-dialog.component.scss'],
  imports: [
    CommonModule,
    TranslocoModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    MatTabsModule,
    MatCardModule,
    MatTooltipModule,
    NgClass,
    InventoryDeclarationNotificationPanelComponent,
    DocumentReviewNotificationPanelComponent,
    DocumentExpiryNotificationPanelComponent,
    OrderInquiryNotificationPanelComponent,
    SurveyNotificationPanelComponent
  ],
  providers: [StartProcessService, IdToNamePipe]
})
export class NotificationDetailDialogComponent {
  notification: NotificationRow;
  supplier: EquipmentSupplier[] = [];
  isMobile: boolean;
  /** Legacy client-side lookup, still the only source for catalog and order-v2 claim notifications. */
  assetDetails: string;

  detail?: NotificationDetail;
  detailFailed = false;
  /** Data URLs of the process attachments, keyed by `dataFieldId:index`. */
  attachmentUrls: Record<string, string> = {};

  priorityLabels: Record<number, string> = {
    1: 'Niedrig',
    2: 'Normal',
    3: 'Hoch',
    4: 'Dringend'
  };

  statusOptions = NOTIFICATION_STATUS;
  selectedProcessId: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { notification: NotificationRow; skipMarkAsRead?: boolean },
    private readonly dialogRef: MatDialogRef<NotificationDetailDialogComponent>,
    private readonly backendService: BackendService,
    private readonly processHandlerService: ProcessHandlerService,
    private readonly dialog: MatDialog,
    private readonly idToNamePipe: IdToNamePipe,
    public readonly userNotificationService: UserNotificationService,
    private readonly notify: NotificationService,
    private readonly jwtService: JwtService,
    private readonly translate: TranslocoService,
    private readonly protectedImageLoader: ProtectedImageLoaderService,
    mobileService: MobileViewService
  ) {
    this.isMobile = mobileService.detectMobileDevice();
    this.notification = data.notification;

    const isUnread =
      this.notification.status !== 'read' &&
      this.notification.status !== 'dismissed' &&
      this.notification.status !== 'archived';
    if (isUnread && !this.data.skipMarkAsRead) {
      this.userNotificationService.markAsRead(this.notification.id);
      this.notification = { ...this.notification, status: 'read' };
    }

    this.loadDetail();
    this.getAssetDetails();

    if (this.notification.processId) {
      this.backendService.get<'equipment/getSuppliers'>('equipment/suppliers').subscribe((res) => {
        this.supplier = res;
      });
    }
  }

  /**
   * Loads the full detail for whichever notification the dialog was opened with. The row handed in
   * by the caller is only used to render something immediately; every field below comes from the
   * detail endpoint, identical for both entry points.
   */
  private loadDetail(): void {
    this.backendService
      .get<'notification/getNotificationDetail'>('notifications/{id}/detail', {
        path: { id: this.notification.id }
      })
      .subscribe({
        next: (detail) => {
          this.detail = detail;
          // Keep the locally applied "read" state, the detail was requested before the PATCH landed.
          this.notification = { ...detail.notification, status: this.notification.status };
          this.loadAttachments();
        },
        error: () => {
          this.detailFailed = true;
        }
      });
  }

  private loadAttachments(): void {
    for (const attachment of this.detail?.attachments ?? []) {
      const key = this.attachmentKey(attachment);
      this.protectedImageLoader
        .loadWithNonce(attachment.processId, (nonce) =>
          this.backendService.getBlob<'processEngineProcess/getUpload'>('process-engine/process/{id}/file/{dataFieldId}', {
            path: { id: attachment.processId, dataFieldId: attachment.dataFieldId },
            query: { nonce, index: attachment.index }
          })
        )
        .then((url) => {
          this.attachmentUrls[key] = url;
        })
        .catch(() => {
          // A missing file must not break the dialog; the entry is simply not rendered.
        });
    }
  }

  attachmentKey(attachment: NotificationDetail['attachments'][number]): string {
    return `${attachment.dataFieldId}:${attachment.index}`;
  }

  /** Renders `—` for anything empty, so no "undefined" or "[object Object]" reaches the user. */
  orDash(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return EMPTY_VALUE;
    const text = String(value).trim();
    return text.length > 0 ? text : EMPTY_VALUE;
  }

  get employeeName(): string {
    return this.orDash(this.detail?.employeeContext?.name);
  }

  get employeeShip(): string {
    return this.orDash(this.detail?.employeeContext?.shipName);
  }

  get employeeShipOperator(): string {
    return this.orDash(this.detail?.employeeContext?.shipOperator);
  }

  /** Only worth a card when the backend actually released the crew context to this role. */
  get hasEmployeeContext(): boolean {
    return !!this.detail?.employeeContext;
  }

  get reportedItemNames(): string {
    const names = (this.detail?.reportedItems ?? [])
      .map((item) => item.name ?? item.supplierId)
      .filter((name): name is string => !!name);
    return names.length > 0 ? names.join('\n') : EMPTY_VALUE;
  }

  get reportComment(): string {
    return this.orDash(this.detail?.reportComment);
  }

  /**
   * How a PSA Bedarfsmeldung was decided, on the notification the reporting employee gets
   * (SHIP-1850). The backend puts it next to the article in the array metadata; without a sender, so
   * the crew never learns who decided.
   */
  get defectReportResult(): { result: 'approved' | 'rejected'; rejectionReason: string | null } | undefined {
    const metadata: unknown = this.notification.metadata;
    if (!Array.isArray(metadata)) return undefined;

    const entry = metadata.find(
      (item): item is { defectReportResult: 'approved' | 'rejected'; rejectionReason?: string | null } =>
        typeof item === 'object' && item !== null && 'defectReportResult' in item
    );
    if (!entry) return undefined;
    return { result: entry.defectReportResult, rejectionReason: entry.rejectionReason?.trim() || null };
  }

  /**
   * Who resolved the report. Shown for every viewer who may open the notification at all - the
   * dialog already rendered accepted/rejectedBy this way before SHIP-1783, only the backend never
   * filled the columns.
   */
  get processedByName(): string {
    const processedBy = this.detail?.processedBy;
    if (!processedBy) return EMPTY_VALUE;
    if (processedBy.name) return processedBy.name;
    // Account is gone entirely; the decision itself is still on record.
    return processedBy.userId ? this.translate.translate('notifications.detail.processedBy.former_user') : EMPTY_VALUE;
  }

  /**
   * The affected PSA article. Prefers the server-resolved items (PSA defect/problem reports);
   * catalog updates and order-v2 claims still come from the legacy client-side lookup.
   */
  get articleText(): string {
    const fromDetail = this.reportedItemNames;
    return fromDetail !== EMPTY_VALUE ? fromDetail : this.orDash(this.assetDetails);
  }

  /** Only informational notifications may be archived - see {@link UserNotificationService.isArchivable}. */
  get canArchive(): boolean {
    return this.userNotificationService.isArchivable(this.notification);
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  close(): void {
    this.dialogRef.close();
  }

  archive(): void {
    if (!this.canArchive) {
      this.notify.error(this.translate.translate('notifications.detail.archive_blocked'));
      return;
    }

    this.userNotificationService.archiveNotification(this.notification.id).subscribe(() => {
      this.dialogRef.close({ updated: true });
    });
  }

  private getAssetDetails() {
    if (this.notification.title.includes('Catalog') || this.isLegacyEquipmentNotification()) {
      const assetIds = getLegacyEquipmentNotificationAssetIds(this.notification.metadata);
      if (assetIds.length === 0) return;

      this.backendService
        .post<'asset/getAllAssetsGet'>('asset/all/get', {
          body: {
            assetIds,
            paranoid: false
          }
        })
        .subscribe((assets) => {
          this.assetDetails = assets.result.map((a) => a.name).join('\n');
        });
      return;
    }

    const itemId = this.notification.metadata?.orderItemId;
    if (!itemId) return;

    this.backendService
      .get<'orderV2/getOrderItem'>('api/v2/equipment/orders/order-items/{orderItemId}', {
        path: { orderItemId: String(itemId) }
      })
      .subscribe((item) => {
        this.assetDetails = item.equipment.name;
      });
  }

  /** Legacy defect/missing/wrong-size notifications from PE order flow — remove when legacy orders are finished. */
  private isLegacyEquipmentNotification(): boolean {
    return isLegacyEquipmentDefectMissingNotification(this.notification);
  }

  isInventoryDeclarationNotification(): boolean {
    return isInventoryDeclarationNotification(this.notification);
  }

  isDocumentReviewNotification(): boolean {
    return this.notification.notificationType?.key === 'document_review';
  }

  isDocumentExpiryNotification(): boolean {
    return this.notification.notificationType?.key === 'document_expiry';
  }

  isOrderInquiryNotification(): boolean {
    return ORDER_INQUIRY_NOTIFICATION_TYPE_KEYS.includes(this.notification.notificationType?.key ?? '');
  }

  isSurveyNotification(): boolean {
    return this.notification.notificationType?.key === SURVEY_NOTIFICATION_TYPE_KEY;
  }

  onSurveyAnswered(): void {
    this.dialogRef.close({ updated: true });
  }

  onOrderInquiryAnswered(): void {
    this.dialogRef.close({ updated: true });
  }

  onInventoryDeclarationResolved(): void {
    this.dialogRef.close({ updated: true });
  }

  onDocumentReviewResolved(): void {
    this.dialogRef.close({ updated: true });
  }

  onDocumentExpiryUploaded(): void {
    this.dialogRef.close({ updated: true });
  }

  resumeProcess(processId: number) {
    this.backendService
      .post<'processEngineProcess/getAllActiveSteps'>('process-engine/process/getAllActiveSteps', {
        body: {
          onlyInputSteps: true,
          includeProcessDataFields: true,
          criteria: {
            process_id: processId
          }
        }
      })
      .subscribe({
        next: (steps) => {
          const userRoleId = this.jwtService.userData?.role?.id;
          const activeStep = (steps.result ?? [])
            .map((entry) => entry as unknown as Step)
            .find((step) => isProcessStepResponsibleForUser(step, userRoleId));

          if (!activeStep) {
            this.notify.error(this.translate.translate('notifications.detail.no_active_process_step'));
            return;
          }

          this.openResumeStep(activeStep);
        },
        error: () => {
          this.notify.error(this.translate.translate('notifications.detail.process_resume_failed'));
        }
      });
  }

  private async openResumeStep(step: Step): Promise<void> {
    try {
      const isLegacy =
        isLegacyEquipmentDefectMissingNotification(this.notification) ||
        LEGACY_EQUIPMENT_SUPPLIER_CONTACT_PROCESS_DEFS.includes(step.process?.process_definition_id ?? -1);

      if (isLegacy) {
        await this.ensureSuppliersLoaded();
        const assetIds = getLegacyEquipmentNotificationAssetIds(this.notification.metadata);
        const assets =
          assetIds.length > 0
            ? (
                await firstValueFrom(
                  this.backendService.post<'asset/getAllAssetsGet'>('asset/all/get', {
                    body: { assetIds, paranoid: false }
                  })
                )
              ).result
            : [];
        await this.openProcessHandlerDialog(step, assets as unknown as Asset[]);
        return;
      }

      await this.openProcessHandlerDialogV2(step);
    } catch {
      this.notify.error(this.translate.translate('notifications.detail.process_resume_failed'));
    }
  }

  private async ensureSuppliersLoaded(): Promise<void> {
    if (this.supplier.length > 0) return;
    this.supplier = await firstValueFrom(this.backendService.get<'equipment/getSuppliers'>('equipment/suppliers'));
  }

  /** Legacy PE supplier-contact steps — remove when legacy orders are finished. */
  private async openProcessHandlerDialog(step: Step, assets: Asset[]): Promise<void> {
    const dimensions = this.processHandlerService.getDialogScreenSize();
    if (this.needsSupplierContactDatafieldPreparation(step)) {
      step = await this.prepareInheritedDatafields(step, assets);
    }

    this.selectedProcessId = step.process.id;
    const dialogRef = this.dialog.open(ProcessHandlerDialogComponent, {
      ...dimensions,
      data: {
        steps: [step],
        processStartOptions: {},
        noTabs: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result instanceof AllStepsPerformedEvent) {
        this.dialogRef.close({ updated: true });
      }
    });
  }

  private async openProcessHandlerDialogV2(step: Step): Promise<void> {
    const dimensions = this.processHandlerService.getDialogScreenSize();

    this.selectedProcessId = step.process.id;
    const dialogRef = this.dialog.open(ProcessHandlerDialogComponent, {
      ...dimensions,
      data: {
        steps: [step],
        processStartOptions: {},
        noTabs: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result instanceof AllStepsPerformedEvent) {
        this.dialogRef.close({ updated: true });
      }
    });
  }

  private async prepareInheritedDatafields(step: Step, assets: Asset[]): Promise<Step> {
    const updatedStep = JSON.parse(JSON.stringify(step)) as Step;
    const dataFields = updatedStep.process?.DataFields ?? [];
    const isDefect = step.process?.id_code_1?.includes('defect');

    const stepArticleId = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_ARTICLE_NUMBER);
    if (isDefect) {
      const articleData = (dataFields.find((df) => df.name === DF_ORDER_DEFECT_ARTICLES)?.value as { id: string; file?: string }[]) ?? [];
      const image = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_PROOF_IMAGE);
      const assetSerials = assets.map((a) => a.lsSerialNumber);
      const filteredArticles = articleData.filter((ad) => assetSerials.includes(ad.id));
      if (image) {
        image.value = filteredArticles.map((ad) => ad.file);
      }
      if (stepArticleId) {
        stepArticleId.value = filteredArticles.map((ad) => ad.file).toString();
      }
    } else {
      const articleData = (dataFields.find((df) => df.name === DF_ORDER_MISSING_ARTICLES)?.value as { id: string }[]) ?? [];
      if (stepArticleId) {
        stepArticleId.value = articleData.map((ad) => ad.id).toString();
      }
    }

    const orderNumber = dataFields.find((df) => df.name === DF_SHARED_ORDER_NUMBER)?.value;
    const stepOrderNumber = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_ORDER_NUMBER);
    if (stepOrderNumber && orderNumber !== undefined) {
      stepOrderNumber.value = orderNumber;
    }

    const phoneUser = dataFields.find((df) => df.name === DF_ORDER_PHONE)?.value;
    const stepPhoneUser = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_USER_PHONE);
    if (stepPhoneUser && phoneUser !== undefined) {
      stepPhoneUser.value = phoneUser;
    }

    const userId = dataFields.find((df) => df.name === DF_SHARED_VISITOR)?.value;
    if (userId !== undefined) {
      const userName = await firstValueFrom(this.idToNamePipe.transform(userId));
      const stepName = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_USER_NAME);
      if (stepName) {
        stepName.value = userName;
      }
    }

    const articleField = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_ARTICLE);
    if (articleField) {
      articleField.value = assets.map((a) => a.name).toString();
    }

    const phoneField = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_PHONE);
    if (phoneField) {
      phoneField.value = '+49 2361 5805 436';
    }

    const contactField = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_CONTACT);
    if (contactField) {
      contactField.value = 'Tobias Kyrmel';
    }

    const supplierField = dataFields.find((df) => df.name === DF_SUPPLIER_CONTACT_SUPPLIER);
    if (supplierField) {
      supplierField.value = assets.map((a) => this.getSupplierName(a.ownership)).toString();
    }

    return updatedStep;
  }

  private needsSupplierContactDatafieldPreparation(step: Step): boolean {
    if (!LEGACY_EQUIPMENT_SUPPLIER_CONTACT_PROCESS_DEFS.includes(step.process?.process_definition_id ?? -1)) {
      return false;
    }
    const dataFields = step.process?.DataFields ?? [];
    return dataFields.some((df) => df.name === DF_SUPPLIER_CONTACT_ARTICLE_NUMBER);
  }

  private getSupplierName(id: number): string {
    const match = this.supplier.find((sup) => sup.id === id);
    if (match) return match.name;
    if (this.supplier.length > 0) return this.supplier[0].name;
    return String(id);
  }

  getStatusLabel(status: string): string {
    return this.statusOptions.find((s) => s.value === status)?.label || status;
  }

  getPriorityClass(priority: number): string {
    const priorityMap: Record<number, string> = {
      1: 'text-muted',
      2: 'text-primary',
      3: 'text-warn',
      4: 'text-error'
    };
    return priorityMap[priority] || '';
  }

  getPriorityIcon(priority: number): string {
    const iconMap: Record<number, string> = {
      1: 'signal_cellular_alt_1_bar',
      2: 'signal_cellular_alt_2_bar',
      3: 'signal_cellular_alt',
      4: 'signal_cellular_4_bar_alert'
    };
    return iconMap[priority] || 'signal_cellular_alt_2_bar';
  }

  getTargetDisplay(): string {
    switch (this.notification.targetType) {
      case 'user':
        return this.notification.targetUser
          ? `${this.notification.targetUser.firstName} ${this.notification.targetUser.lastName} (${this.notification.targetUser.email})`
          : this.orDash(null);
      case 'role':
        return this.orDash(this.notification.targetRole?.name);
      case 'group':
        return `Group: ${this.notification.targetGroupId}`;
      case 'all':
        return 'All Users';
      default:
        return this.notification.targetType;
    }
  }

  getTargetIcon(): string {
    const iconMap: Record<string, string> = {
      user: 'person',
      role: 'group',
      group: 'groups',
      all: 'public'
    };
    return iconMap[this.notification.targetType] || 'help';
  }

  isExpired(): boolean {
    return this.notification.expiresAt && new Date(this.notification.expiresAt) < new Date();
  }

  /** Resolved server-side; falls back to the raw id only while the detail is still loading. */
  getNotesModifiedByNames(): string {
    const resolved = this.detail?.notesModifiedBy ?? [];
    if (resolved.length > 0) {
      return resolved.map((entry) => this.orDash(entry.name)).join(', ');
    }
    const ids = this.notification.notesModifiedByUserIds ?? [];
    return ids.map((id: number) => `#${id}`).join(', ');
  }
}
